import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

/** User-facing feature flags — [settings field, PATCH path, title, description]. */
const FLAGS = [
  [
    'liveQuotePageEnabled',
    '/admin/settings/live-quote-page',
    'Live quote page (/stock/:symbol)',
    'Users tapping a symbol or a market-view row open a live quote page that polls Finnhub/Yahoo '
      + 'every 2 minutes while open. Turn this off to cut that API volume — users will still see '
      + 'signal prices, just not the standalone live quote page.',
  ],
  [
    'marketPageEnabled',
    '/admin/settings/market-page',
    'Market page (/market)',
    'Indexes, movers and pre/post-market board. Turn this off to remove Market from the users\' menu '
      + 'and block its data for them.',
  ],
  [
    'registrationEnabled',
    '/admin/settings/registration',
    'New registrations',
    'Off hides "Create an account" on the sign-in page and the server refuses new sign-ups '
      + '(including old /register and referral links). Existing users can still sign in.',
  ],
];

function SiteSettings() {
  const [flags, setFlags] = useState({ liveQuotePageEnabled: true, marketPageEnabled: true, registrationEnabled: true });
  const [refreshMinutes, setRefreshMinutes] = useState(10);
  const [extraSymbols, setExtraSymbols] = useState('');   // edit box
  const [savedExtra, setSavedExtra] = useState([]);        // what the server has
  const [maxExtra, setMaxExtra] = useState(20);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api.get('/admin/settings')
      .then(r => {
        setFlags({
          liveQuotePageEnabled: r.data?.liveQuotePageEnabled !== false,
          marketPageEnabled: r.data?.marketPageEnabled !== false,
          registrationEnabled: r.data?.registrationEnabled !== false,
        });
        setRefreshMinutes(Number(r.data?.signalPriceRefreshMinutes) || 10);
        const extra = Array.isArray(r.data?.universeExtraSymbols) ? r.data.universeExtraSymbols : [];
        setSavedExtra(extra);
        setExtraSymbols(extra.join(', '));
        setMaxExtra(Number(r.data?.maxExtraSymbols) || 20);
      })
      .finally(() => setLoading(false));
  }, []);

  const saveInterval = async (minutes) => {
    setSaving('interval');
    setMsg('');
    try {
      const r = await api.patch(`/admin/settings/signal-price-refresh?minutes=${minutes}`);
      setRefreshMinutes(r.data?.signalPriceRefreshMinutes ?? minutes);
      setMsg(r.data?.message || 'Saved');
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(null);
    }
  };

  const saveExtraSymbols = async () => {
    setSaving('extra');
    setMsg('');
    try {
      const r = await api.put(`/admin/settings/universe-extra?symbols=${encodeURIComponent(extraSymbols)}`);
      const list = r.data?.universeExtraSymbols || [];
      setSavedExtra(list);
      setExtraSymbols(list.join(', '));
      setMsg(r.data?.message || 'Saved');
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(null);
    }
  };

  const toggle = async (key, path) => {
    const next = !flags[key];
    setSaving(key);
    setMsg('');
    try {
      const r = await api.patch(`${path}?enabled=${next}`);
      setFlags(f => ({ ...f, [key]: r.data?.[key] ?? next }));
      setMsg(r.data?.message || 'Saved');
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-8">
      <h2 className="text-sm font-semibold text-slate-800 mb-1">Site settings</h2>
      <p className="text-xs text-slate-400 mb-4">Feature flags for regular users. Admin &amp; semi-admin accounts are never affected.</p>

      <div className="space-y-3">
        {FLAGS.map(([key, path, title, desc]) => {
          const on = flags[key];
          return (
            <div key={key} className="flex items-center justify-between gap-4 border border-slate-100 rounded-xl p-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800">{title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
              </div>
              <button
                type="button"
                disabled={loading || saving != null}
                onClick={() => toggle(key, path)}
                className={`relative shrink-0 w-12 h-7 rounded-full transition-colors disabled:opacity-50 ${
                  on ? 'bg-emerald-500' : 'bg-slate-300'
                }`}
                aria-pressed={on}
                title={on ? 'Enabled for users — click to disable' : 'Disabled for users — click to enable'}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                    on ? 'translate-x-5' : ''
                  }`}
                />
              </button>
            </div>
          );
        })}

        {/* Admin-added symbols on top of the built-in 130-stock scan list. */}
        <div className="border border-slate-100 rounded-xl p-4">
          <p className="text-sm font-medium text-slate-800">Extra symbols to scan</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Added to the built-in 130-stock list used by Option Candidates (POC), Rapid Move and the Market movers —
            up to {maxExtra}, separated by commas or spaces. Use liquid, optionable stocks (e.g. CRDO, LITE, ARM).
            Each one costs about 390 marketdata.app credits a day. Leave empty to scan only the built-in list.
          </p>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <input
              type="text"
              value={extraSymbols}
              onChange={(e) => setExtraSymbols(e.target.value.toUpperCase())}
              placeholder="e.g. CRDO, LITE, COHR, ARM, HIMS"
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
            />
            <button
              type="button"
              disabled={loading || saving != null || extraSymbols.trim() === savedExtra.join(', ')}
              onClick={saveExtraSymbols}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {saving === 'extra' ? 'Saving…' : 'Save'}
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            {savedExtra.length ? `Now scanning ${savedExtra.length} extra: ${savedExtra.join(', ')}` : 'No extra symbols'}
          </p>
        </div>

        {/* How often users' Signals page prices refresh while Live quote is OFF. */}
        <div className="flex flex-wrap items-center justify-between gap-4 border border-slate-100 rounded-xl p-4">
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-800">Signal prices when Live quote is off</p>
            <p className="text-xs text-slate-500 mt-0.5">
              Users still see prices on the Signals page, refreshed on this interval (shared by everyone,
              so data-provider calls don&apos;t grow with users). Live quote on: every 30 seconds as usual.
            </p>
          </div>
          <div className="flex shrink-0 gap-1 rounded-xl bg-slate-100 p-1">
            {[5, 10, 15].map((m) => (
              <button
                key={m}
                type="button"
                disabled={loading || saving != null}
                onClick={() => saveInterval(m)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition disabled:opacity-50 ${
                  refreshMinutes === m ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {m} min
              </button>
            ))}
          </div>
        </div>
      </div>
      {msg && <p className="text-xs text-slate-500 mt-2">{msg}</p>}
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState({ activeUsers: 0, premiumUsers: 0 });
  const [md, setMd] = useState(null); // marketdata.app credit usage

  useEffect(() => {
    api.get('/admin/stats').then(r => setStats(r.data)).catch(() => {});
    api.get('/admin/marketdata-usage').then(r => setMd(r.data)).catch(() => {});
  }, []);

  const mdUsed = md?.creditsUsed;
  const mdLimit = md?.creditsLimit;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Admin Dashboard</h1>
      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <p className="text-xs text-slate-400 uppercase tracking-wide">Active Users</p>
          <p className="text-3xl font-bold mt-1">{stats.activeUsers}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <p className="text-xs text-slate-400 uppercase tracking-wide">Premium Subs</p>
          <p className="text-3xl font-bold mt-1 text-emerald-600">{stats.premiumUsers}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 sm:col-span-2">
          <p className="text-xs text-slate-400 uppercase tracking-wide">marketdata.app credits today</p>
          <p className="text-3xl font-bold mt-1">
            {mdUsed != null && mdLimit != null ? `${mdUsed.toLocaleString()} / ${mdLimit.toLocaleString()}` : '—'}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {md?.guardActive
              ? `Reserve reached (${md.reserveCredits.toLocaleString()} left) — using free fallbacks until the reset.`
              : `${(md?.requestsToday ?? 0).toLocaleString()} requests today (ET) · fallbacks kick in under ${(md?.reserveCredits ?? 0).toLocaleString()} credits left`}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 mb-8">
        <Link to="/admin/signals" className="bg-slate-900 text-white px-5 py-2.5 rounded-xl text-sm font-medium">Manage Signals</Link>
        <Link to="/admin/users" className="bg-slate-100 text-slate-800 px-5 py-2.5 rounded-xl text-sm font-medium">Manage Users</Link>
        <Link to="/admin/pricing" className="bg-emerald-600 text-white px-5 py-2.5 rounded-xl text-sm font-medium">Plan pricing</Link>
        <Link to="/admin/signal-history" className="bg-slate-100 text-slate-800 px-5 py-2.5 rounded-xl text-sm font-medium">Signal History</Link>
        <Link to="/admin/market" className="bg-slate-100 text-slate-800 px-5 py-2.5 rounded-xl text-sm font-medium">Market</Link>
        <Link to="/news" className="bg-slate-100 text-slate-800 px-5 py-2.5 rounded-xl text-sm font-medium">News</Link>
      </div>
      <SiteSettings />
    </div>
  );
}
